import { useWindowResize } from 'hooks';
import { Image, Typography } from 'lib/components';
import React, { useEffect, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import ReactDom from 'react-dom';
import { JobNote } from 'types';
import adjustTime from '../../lib/adjustTime';
import { useSelectSettings } from '../../store';
import classes from './index.module.scss';

/**
 * Props for the Notes component
 */
interface Props {
	items: JobNote[]; // Array of job notes to display
	deletable?: boolean; // Whether notes can be deleted
	onDelete?: (id: number) => void; // Callback when a note is deleted
}

/**
 * Props for the Note component
 */
interface NoteProps {
	note: JobNote; // The note data to display
	deletable?: boolean; // Whether the note can be deleted
	onDelete?: (id: number) => void; // Callback when the note is deleted
}

/**
 * Props for the NoteViewModal component
 */
interface NoteModalProps {
	isOpen: boolean; // Controls visibility of the modal
	message: string; // The note message to display
	onClose: () => void; // Callback when modal is closed
}

/**
 * Helper function to sort notes by ID in descending order (newest first)
 * Creates a new array to avoid mutating the original
 *
 * @param notes - Array of notes to sort
 * @returns Sorted array of notes
 */
const sortNotes = (notes: JobNote[]) => [...notes].sort((a, b) => b.id - a.id);

/**
 * Modal component for viewing the full content of a note
 * Displays the note message in a modal dialog
 *
 * @param isOpen - Controls visibility of the modal
 * @param onClose - Callback when modal is closed
 * @param message - The note message to display
 */
const NoteViewModal = ({ isOpen, onClose, message }: NoteModalProps) => {
	// Don't render anything if the modal is not open
	if (!isOpen) {
		return null;
	}

	// Render the modal using a portal to ensure it appears above other content
	return ReactDom.createPortal(
		<div className={classes['note-view-modal']}>
			<div className={classes['note-view-modal--content']}>
				{/* Close button */}
				<button className={classes['note-view-modal--close']} onClick={onClose}>
					x
				</button>
				{/* Note message */}
				<div className={classes['note-view-modal--message']}>
					<Typography>{message}</Typography>
				</div>
			</div>
		</div>,
		document.getElementById('portal') as HTMLElement
	);
};

/**
 * Component for displaying a single job note
 * Shows note ID, creation time, and provides delete functionality if enabled
 * Clicking on the note opens a modal with the full note content
 *
 * @param note - The note data to display
 * @param deletable - Whether the note can be deleted
 * @param onDelete - Callback when the note is deleted
 */
const Note: React.FC<NoteProps> = ({ note, deletable, onDelete }: NoteProps) => {
	// Settings data from Redux store (for time zone adjustments)
	const settingsState = useSelectSettings();

	// Responsive design helpers
	const { isSmallerDevice } = useWindowResize();
	// Controls visibility of the note view modal
	const [isOpen, setIsOpen] = useState(false);

	/**
	 * Handle note deletion
	 * Calls the onDelete callback with the note ID
	 */
	const handleDelete = () => {
		onDelete && onDelete(note.id);
	};

	return (
		<>
			{/* Modal for viewing the full note content */}
			<NoteViewModal isOpen={isOpen} message={note.message} onClose={() => setIsOpen(false)} />

			{/* Note container */}
			<div
				className={classes['note-container']}
				style={{
					width: '96%',
					justifyContent: 'space-between',
				}}
			>
				{/* Note content and metadata - clickable to open the full view */}
				<div
					onClick={() => setIsOpen(true)}
					style={{
						width: deletable ? '90%' : '100%',
						display: 'flex',
						justifyContent: 'space-between',
						cursor: 'pointer',
					}}
				>
					{/* Note ID */}
					<Typography variant={isSmallerDevice ? 'h5' : 'h6'} color="gray">
						Nurse Note #{note.id}
					</Typography>

					{/* Note creation time - adjusted for time zone */}
					<Typography variant={isSmallerDevice ? 'h5' : 'h6'} color="gray" style={{ marginLeft: '0.2rem' }}>
						{adjustTime(settingsState, note.createdAt, 'MM/DD/YYYY - hh:mm A').format('MM/DD/YYYY hh:mm A')}
					</Typography>
				</div>

				{/* Delete button - only shown if deletable is true */}
				{deletable && <Image src="icTrashDanger" style={{ cursor: 'pointer' }} onClick={handleDelete} />}
			</div>
		</>
	);
};

/**
 * Component for displaying a list of job notes
 * Sorts notes by ID (newest first) and renders them in a scrollable container
 *
 * @param items - Array of job notes to display
 * @param deletable - Whether notes can be deleted
 * @param onDelete - Callback when a note is deleted
 */
const Notes = ({ items, deletable, onDelete }: Props) => {
	// State for sorted notes (newest first)
	const [sortedNotes, setSortedNotes] = useState<JobNote[]>([]);

	/**
	 * Sort notes when items prop changes
	 * Ensures notes are always displayed newest first
	 */
	useEffect(() => {
		if (items.length === 0) return;

		setSortedNotes(sortNotes(items));
	}, [items]);

	return (
		<>
			{/* Scrollable container for notes */}
			<Scrollbars
				renderThumbVertical={(props) => <div {...props} className={classes['vertical-scroll-thumb']} />}
			>
				{/* Render each note with appropriate props */}
				{sortedNotes.map((note) => (
					<Note key={note.id} note={note} deletable={deletable} onDelete={onDelete} />
				))}
			</Scrollbars>
		</>
	);
};

export default Notes;
